import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  GroupOff as GroupOffIcon,
  VpnKey as VpnKeyIcon,
  ListAlt as ListAltIcon,
  PhotoLibrary as PhotoLibraryIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Cable as CableIcon,
  Videocam as VideocamIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Palette as PaletteIcon,
  CloudUpload as CloudUploadIcon,
  Security as SecurityIcon,
  Block as IntrusionAttendanceIcon,
  NoAccounts as IntrusionProfileIcon,
  DomainDisabled as IntrusionAccountsIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  FaceRetouchingNatural as FaceRetouchingNaturalIcon,
  Assessment as AssessmentIcon,
  VideoCameraFront as VideoCameraFrontIcon,
  Tune as TuneIcon,
  Menu as MenuIcon,
  DirectionsRun as DirectionsRunIcon,
  VolumeUp as VolumeUpIcon,
  Campaign as CampaignIcon,
  AssignmentInd as AssignmentIndIcon,
  Badge as BadgeIcon,
  CameraAlt as CameraAltIcon,
  MoreTime as MoreTimeIcon,
  Notifications as NotificationsIcon,
  Storage as StorageIcon,
  SmartToy as SmartToyIcon,
  ManageAccounts as ManageAccountsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  AccountTree as AccountTreeIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { buildApiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { getActivityCameras } from '../utils/api';

const drawerWidth = 280;
const collapsedDrawerWidth = 84;
const SIDEBAR_TRANSITION_DURATION_MS = 320;
const SIDEBAR_TRANSITION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

const removeTopLevelDebugMenu = (items) => items.filter((item) => item.text !== 'Debug');

const VISION_REVIEW_SUBITEM = {
  text: 'AI Matching',
  path: '/ai-review',
  icon: <SmartToyIcon />,
};

const VISION_REVIEW_STATS_SUBITEM = {
  text: 'AI Matching Statistics',
  path: '/ai-review/statistics',
  icon: <AssessmentIcon />,
};

const injectVisionReviewMenu = (items, available) => {
  if (!available) return items;
  const settingsIndex = items.findIndex((item) => item.text === 'Settings');
  if (settingsIndex === -1) return items;
  const settings = items[settingsIndex];
  const subItems = Array.isArray(settings.subItems) ? settings.subItems : [];
  const nextSubItems = [...subItems];
  if (!nextSubItems.some((s) => s.path === VISION_REVIEW_SUBITEM.path)) {
    nextSubItems.push(VISION_REVIEW_SUBITEM);
  }
  if (!nextSubItems.some((s) => s.path === VISION_REVIEW_STATS_SUBITEM.path)) {
    nextSubItems.push(VISION_REVIEW_STATS_SUBITEM);
  }
  if (
    nextSubItems.length === subItems.length
    && subItems.some((s) => s.path === VISION_REVIEW_SUBITEM.path)
    && subItems.some((s) => s.path === VISION_REVIEW_STATS_SUBITEM.path)
  ) {
    return items;
  }
  const result = [...items];
  result[settingsIndex] = {
    ...settings,
    hasDropdown: true,
    subItems: nextSubItems,
  };
  return result;
};

const SIDEBAR_TAB_ORDER = [
  'Dashboard',
  'Attendance',
  'Logs',
  'Intrusion',
  'Unmatched Groups',
  'People',
  'Users',
  'User Management',
  'Recordings',
  'Settings',
  'Test',
];

/** Routes that belong under the People sidebar group (dropdownKey: employees). */
const isPeopleSidebarRoute = (pathname) => {
  const p = pathname || '';
  return (
    p === '/employees' ||
    p.startsWith('/employees/') ||
    p.startsWith('/employee/') ||
    p === '/vendors' ||
    p.startsWith('/vendors/') ||
    p.startsWith('/vendor/') ||
    p === '/visitors' ||
    p.startsWith('/visitors/') ||
    p.startsWith('/visitor/') ||
    p === '/intrusion-profile' ||
    p.startsWith('/intrusion-profile/')
  );
};

/** Routes that belong under the Attendance sidebar group (dropdownKey: attendance). */
const isAttendanceSidebarRoute = (pathname) => {
  const p = pathname || '';
  return (
    p === '/present' ||
    p.startsWith('/present/') ||
    p === '/activity-tracking' ||
    p.startsWith('/activity-tracking/') ||
    p === '/unavailable' ||
    p.startsWith('/unavailable/') ||
    p === '/export-to-kivo' ||
    p.startsWith('/export-to-kivo/')
  );
};

/** Routes that belong under the Logs sidebar group (dropdownKey: logs). */
const isLogsSidebarRoute = (pathname) => {
  const p = pathname || '';
  return (
    p === '/in-out' ||
    p.startsWith('/in-out/') ||
    p === '/activity' ||
    p.startsWith('/activity/')
  );
};

/** Routes that belong under the Dashboard sidebar group (dropdownKey: dashboard). */
const isDashboardSidebarRoute = (pathname) => {
  const p = pathname || '';
  return (
    p === '/dashboard' ||
    p.startsWith('/dashboard/') ||
    p === '/intrusion-dashboard' ||
    p.startsWith('/intrusion-dashboard/')
  );
};

const Sidebar = ({ mobileOpen, handleDrawerToggle, desktopCollapsed = false, onDesktopCollapseToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktopCollapsed = !isMobile && desktopCollapsed;
  const currentDrawerWidth = isDesktopCollapsed ? collapsedDrawerWidth : drawerWidth;
  const navigate = useNavigate();
  const location = useLocation();
  const [showSpinner, setShowSpinner] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [employeesOpen, setEmployeesOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [backendVersion, setBackendVersion] = useState(process.env.REACT_APP_BACKEND_VERSION || '---');
  const [menuItems, setMenuItems] = useState([]);
  const [activityTrackingRunning, setActivityTrackingRunning] = useState(false);
  const { permissions: userPermissions, isAdmin } = useAuth();

  const visionReviewAvailable = userPermissions?.feature_flags?.vision_review === true;

  useEffect(() => {
    const t = setTimeout(() => setShowSpinner(false), 1000); // 2000ms = 2 seconds
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const loadBackendVersion = async () => {
      try {
        const response = await fetch(buildApiUrl('/version'));
        if (!response.ok) return;
        const payload = await response.json();
        if (payload?.version) {
          setBackendVersion(payload.version);
        }
      } catch (_) {
        // Keep fallback version if backend version endpoint is unavailable.
      }
    };
    loadBackendVersion();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadActivityTrackingStatus = async () => {
      try {
        const response = await getActivityCameras();
        const cameras = Array.isArray(response?.cameras) ? response.cameras : [];
        const running = cameras.some((camera) => Boolean(camera?.stream_running));
        if (!cancelled) setActivityTrackingRunning(running);
      } catch (_) {
        if (!cancelled) setActivityTrackingRunning(false);
      }
    };

    loadActivityTrackingStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshActivityTrackingStatus = useCallback(async () => {
    try {
      const response = await getActivityCameras();
      const cameras = Array.isArray(response?.cameras) ? response.cameras : [];
      setActivityTrackingRunning(cameras.some((camera) => Boolean(camera?.stream_running)));
    } catch (_) {
      setActivityTrackingRunning(false);
    }
  }, []);

  useEffect(() => {
    const onTrackingStatusChanged = () => {
      refreshActivityTrackingStatus();
    };
    window.addEventListener('activity-tracking-status-changed', onTrackingStatusChanged);
    return () => window.removeEventListener('activity-tracking-status-changed', onTrackingStatusChanged);
  }, [refreshActivityTrackingStatus]);

  // Convert backend menu items to frontend format with icons when permissions change
  useEffect(() => {
    if (userPermissions && Array.isArray(userPermissions.menu_items)) {
      const rawSettingsMenu = userPermissions.menu_items.find((item) => item.text === 'Settings');

      // Convert backend menu items to frontend format with icons
      const menuItemsWithIcons = userPermissions.menu_items.map(item => {
          const iconMap = {
            'Dashboard': <DashboardIcon />,
            'Attendance Dashboard': <DashboardIcon />,
            'Intrusion Dashboard': <SecurityIcon />,
            'Attendance': <ListAltIcon />,
            'Present': <PeopleIcon />,
            'Activity': <DirectionsRunIcon />,
            'Unavailable': <GroupOffIcon />,
            'Unmatched Groups': <GroupOffIcon />,
            'Export to Kivo': <CloudUploadIcon />,
            'Intrusion': <IntrusionAttendanceIcon />,
            'Intrusion Logs': <IntrusionAttendanceIcon />,
            'Logs': <AssessmentIcon />,
            'Audit Logs': <AssessmentIcon />,
            'Activity Logs': <DirectionsRunIcon />,
            'Live Monitoring': <VideocamIcon />,
            'People': <PeopleIcon />,
            'Employees': <PeopleIcon />,
            'Vendors': <AssignmentIndIcon />,
            'Visitors': <BadgeIcon />,
            'Intrusion Profile': <IntrusionProfileIcon />,
            'User Management': <ManageAccountsIcon />,
            'Access Control': <AdminPanelSettingsIcon />,
            'Users': <AccountCircleIcon />,
            'Settings': <SettingsIcon />,
            'Intrusion Account': <IntrusionAccountsIcon />,
            'API Keys': <VpnKeyIcon />,
            'Branch Configuration': <AccountTreeIcon />,
            'Connect with Kivo': <CableIcon />,
            'Camera & Configuration': <CameraAltIcon />,
            'Camera Configuration': <CameraAltIcon />,
            'System Health Check': <HealthAndSafetyIcon />,
            'Detection Setting': <TuneIcon />,
            'Face Embedding Config': <FaceRetouchingNaturalIcon />,
            'Late Entry Config': <MoreTimeIcon />,
            'Recordings': <VideoCameraFrontIcon />,
            'Banner Setting': <PaletteIcon />,
            'Banner setting': <PaletteIcon />,
            'Speaker configuration': <VolumeUpIcon />,
            'Hooter Configuration': <CampaignIcon />,
            'Notification Settings': <NotificationsIcon />,
            'AI Matching': <SmartToyIcon />,
            'Alert Rules': <NotificationsIcon />,
            'Alert Setting': <NotificationsIcon />,
            'Alerts': <NotificationsIcon />,
            'Test': <PhotoLibraryIcon />,
            'DB Explorer': <StorageIcon />,
          };

          const icon = iconMap[item.text] || <DashboardIcon />;

          return {
            ...item,
            icon: icon,
            subItems: item.subItems ? item.subItems.map(subItem => ({
              ...subItem,
              icon:
                subItem.path === '/alerts' || subItem.path === '/settings/alerts'
                  ? <NotificationsIcon />
                  : iconMap[subItem.text] || <PeopleIcon />,
            })) : undefined,
          };
        });

      const orderedMenuItems = menuItemsWithIcons
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
          const aOrder = SIDEBAR_TAB_ORDER.indexOf(a.item?.text);
          const bOrder = SIDEBAR_TAB_ORDER.indexOf(b.item?.text);
          const aRank = aOrder === -1 ? Number.MAX_SAFE_INTEGER : aOrder;
          const bRank = bOrder === -1 ? Number.MAX_SAFE_INTEGER : bOrder;
          if (aRank !== bRank) return aRank - bRank;
          return a.index - b.index; // Keep backend order for tabs not in SIDEBAR_TAB_ORDER.
        })
        .map(({ item }) => item);
      const withVisionReview = injectVisionReviewMenu(
        removeTopLevelDebugMenu(orderedMenuItems),
        userPermissions?.feature_flags?.vision_review === true,
      );
      setMenuItems(withVisionReview);
    } else {
      setMenuItems([]);
    }
  }, [userPermissions]);

  // Auto-expand dropdowns when a sub-item is active
  useEffect(() => {
    if (isDashboardSidebarRoute(location.pathname)) {
      setDashboardOpen(true);
    } else {
      setDashboardOpen(false);
    }
    if (isAttendanceSidebarRoute(location.pathname)) {
      setAttendanceOpen(true);
    } else {
      setAttendanceOpen(false);
    }
    if (isLogsSidebarRoute(location.pathname)) {
      setLogsOpen(true);
    } else {
      setLogsOpen(false);
    }
    
    if (isPeopleSidebarRoute(location.pathname)) {
      setEmployeesOpen(true);
    } else {
      setEmployeesOpen(false);
    }
    if (
      location.pathname === '/user-management' ||
      location.pathname === '/access-control' ||
      location.pathname === '/users'
    ) {
      setUsersOpen(true);
    } else {
      setUsersOpen(false);
    }
    if (location.pathname === '/api-keys' || location.pathname === '/connect-kivo' ||
        location.pathname === '/branch-configuration' ||
        location.pathname === '/camera-streams' || location.pathname === '/banner-settings' ||
        location.pathname === '/notification-settings' ||
        location.pathname === '/intrusion-accounts' ||
        location.pathname === '/ai-review' ||
        location.pathname === '/ai-review/statistics' ||
        location.pathname === '/alerts' ||
        location.pathname === '/settings/alerts' ||
        location.pathname === '/db-explorer' ||
        location.pathname === '/system-health-check' || location.pathname === '/detection-settings' ||
        location.pathname === '/face-embedding-config' ||
        location.pathname === '/late-entry-config' ||
        location.pathname === '/speaker-configuration' ||
        location.pathname === '/hooter-configuration' ||
        location.pathname === '/activity-settings') {
      setSettingsOpen(true);
    } else {
      setSettingsOpen(false);
    }
  }, [location.pathname]);

  // Use menu items from backend (already filtered by user type)
  const visibleMenuItems = menuItems;

  const handleNavigation = (path) => {
    // Check if route is allowed based on backend permissions
    if (userPermissions && Array.isArray(userPermissions.allowed_routes) && !userPermissions.allowed_routes.includes(path)) {
      // Route not allowed, redirect to user's dashboard
      navigate(userPermissions.dashboard_path);
      if (isMobile && handleDrawerToggle) {
        handleDrawerToggle();
      }
      return;
    }

    navigate(path);
    // Close mobile drawer after navigation
    if (isMobile && handleDrawerToggle) {
      handleDrawerToggle();
    }
  };

  const { logout: logoutContext } = useAuth();

  const handleLogout = () => {
    logoutContext();
    navigate('/login');
  };

  const handleToggleDropdown = (key) => {
    if (isDesktopCollapsed && onDesktopCollapseToggle) {
      onDesktopCollapseToggle();
      if (key === 'settings') setSettingsOpen(true);
      if (key === 'dashboard') setDashboardOpen(true);
      if (key === 'attendance') setAttendanceOpen(true);
      if (key === 'logs') setLogsOpen(true);
      if (key === 'employees') setEmployeesOpen(true);
      if (key === 'users') setUsersOpen(true);
      return;
    }

    if (key === 'settings') {
      setSettingsOpen(!settingsOpen);
    } else if (key === 'dashboard') {
      setDashboardOpen(!dashboardOpen);
    } else if (key === 'attendance') {
      setAttendanceOpen(!attendanceOpen);
    } else if (key === 'logs') {
      setLogsOpen(!logsOpen);
    } else if (key === 'employees') {
      setEmployeesOpen(!employeesOpen);
    } else if (key === 'users') {
      setUsersOpen(!usersOpen);
    }
  };

  const isDropdownOpen = (key) => {
    if (key === 'settings') return settingsOpen;
    if (key === 'dashboard') return dashboardOpen;
    if (key === 'attendance') return attendanceOpen;
    if (key === 'logs') return logsOpen;
    if (key === 'employees') return employeesOpen;
    if (key === 'users') return usersOpen;
    return false;
  };

  const getDisplayLabel = (label) => {
    if (label === 'Audit Logs') return 'In/Out';
    if (label === 'Activity Logs') return 'Activity';
    if (label === 'Camera & Configuration') return 'Camera Configuration';
    if (label === 'Alert Rules') return 'Alert Setting';
    return label;
  };

  const isActive = (path) => {
    // Use dashboard_path from backend permissions for dashboard active state
    if (userPermissions && path === userPermissions.dashboard_path) {
      return location.pathname === userPermissions.dashboard_path;
    }

    if (path === '/present') {
      return location.pathname === '/present' || location.pathname.startsWith('/present/');
    }
    if (path === '/activity-tracking') {
      return (
        location.pathname === '/activity-tracking' ||
        location.pathname.startsWith('/activity-tracking/')
      );
    }
    if (path === '/unavailable') {
      return location.pathname === '/unavailable' || location.pathname.startsWith('/unavailable/');
    }
    if (path === '/export-to-kivo') {
      return location.pathname === '/export-to-kivo' || location.pathname.startsWith('/export-to-kivo/');
    }
    if (path === '/in-out') {
      return location.pathname === '/in-out' || location.pathname.startsWith('/in-out/');
    }
    if (path === '/activity') {
      return location.pathname === '/activity' || location.pathname.startsWith('/activity/');
    }
    if (path === '/intrusion-accounts') {
      return (
        location.pathname === '/intrusion-accounts' ||
        location.pathname.startsWith('/intrusion-accounts/')
      );
    }
    if (path === '/employees') {
      return (
        location.pathname === '/employees' ||
        location.pathname.startsWith('/employee/')
      );
    }
    if (path === '/vendors') {
      return (
        location.pathname === '/vendors' ||
        location.pathname.startsWith('/vendors/') ||
        location.pathname.startsWith('/vendor/')
      );
    }
    if (path === '/intrusion-profile') {
      return (
        location.pathname === '/intrusion-profile' ||
        location.pathname.startsWith('/intrusion-profile/')
      );
    }
    if (path === '/visitors') {
      return (
        location.pathname === '/visitors' ||
        location.pathname.startsWith('/visitors/') ||
        location.pathname.startsWith('/visitor/')
      );
    }
    return location.pathname === path;
  };

  const isSettingsActive = (item) => {
    if (item.hasDropdown && item.subItems) {
      if (item.dropdownKey === 'dashboard') {
        return isDashboardSidebarRoute(location.pathname);
      }
      if (item.dropdownKey === 'attendance') {
        return isAttendanceSidebarRoute(location.pathname);
      }
      if (item.dropdownKey === 'logs') {
        return isLogsSidebarRoute(location.pathname);
      }
      if (item.dropdownKey === 'employees') {
        return isPeopleSidebarRoute(location.pathname);
      }
      if (item.dropdownKey === 'users') {
        return (
          location.pathname === '/user-management' ||
          location.pathname === '/access-control' ||
          location.pathname === '/users'
        );
      }
      return item.subItems.some(subItem => location.pathname === subItem.path || location.pathname.startsWith(subItem.path));
    }
    return false;
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box 
        sx={{ 
          p: { xs: 1, sm: 1 },
          display: 'flex',
          flexDirection: 'row',
          justifyContent: isDesktopCollapsed ? 'center' : 'space-between',
          alignItems: 'center'
        }}
      >
        {(!isDesktopCollapsed || isMobile) && (
          <Box
            component="img"
            src="/kivoeye_logo.png"
            alt="Kivo eye"
            sx={{
              ml: 2,
              height: { xs: 50, sm: 55 },
              width: 'auto',
              display: 'block',
              maxWidth: '60%',
              objectFit: 'contain',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/dashboard')}
          />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!isMobile && (
            <Tooltip title={isDesktopCollapsed ? 'Open sidebar' : 'Close sidebar'} placement="right" arrow>
              <IconButton
                onClick={onDesktopCollapseToggle}
                aria-label={isDesktopCollapsed ? 'Open sidebar' : 'Close sidebar'}
                sx={{
                  color: 'text.primary',
                  p: 0.5,
                  width: 38,
                  height: 38,
                  '&:hover .sidebar-collapsed-logo': {
                    opacity: 0,
                    transform: 'scale(0.72)',
                  },
                  '&:hover .sidebar-collapsed-menu': {
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                }}
              >
                {isDesktopCollapsed ? (
                  <Box sx={{ position: 'relative', width: 26, height: 26 }}>
                    <Box
                      component="img"
                      className="sidebar-collapsed-logo"
                      src="/assets/icons/kivo-fav-icon.png"
                      alt="Kivo"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        opacity: 1,
                        transform: 'scale(1)',
                        transition: 'opacity 180ms ease, transform 180ms ease',
                      }}
                    />
                    <MenuIcon
                      className="sidebar-collapsed-menu"
                      sx={{
                        position: 'absolute',
                        top: 2,
                        left: 2,
                        opacity: 0,
                        transform: 'scale(0.72)',
                        transition: 'opacity 180ms ease, transform 180ms ease',
                      }}
                    />
                  </Box>
                ) : (
                  <MenuIcon sx={{ fontSize: 24 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
          {showSpinner && !isDesktopCollapsed && (
            <CircularProgress
              size={20}
              thickness={7}
              sx={{
                color: '#40518a',
              }}
            />
          )}
          {isMobile && handleDrawerToggle && (
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                color: 'text.primary',
                p: 0.5,
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <Divider />
      <List sx={{ px: { xs: 0.5, sm: 0 }, flex: 1, overflowY: 'auto' }}>
        {visibleMenuItems.length === 0 ? (
          <ListItem disablePadding>
            <ListItemText
              primary="Loading menu..."
              sx={{ px: 2, py: 1, color: 'text.secondary' }}
            />
          </ListItem>
        ) : (
          visibleMenuItems.map((item) => (
          <React.Fragment key={item.text}>
            {item.hasDropdown ? (
              <>
                <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleToggleDropdown(item.dropdownKey)}
                      sx={{
                      justifyContent: isDesktopCollapsed ? 'center' : 'flex-start',
                      backgroundColor: isSettingsActive(item) ? 'primary.main' : 'transparent',
                      color: isSettingsActive(item) ? 'white' : 'text.primary',
                      '&:hover': {
                        backgroundColor: isSettingsActive(item) ? 'primary.dark' : 'action.hover',
                      },
                      mx: { xs: 0.5, sm: 1 },
                      borderRadius: 1,
                      mb: 0.5,
                      py: { xs: 1, sm: 1.5 },
                      px: isDesktopCollapsed ? 0.75 : undefined,
                      transition: `padding ${SIDEBAR_TRANSITION_DURATION_MS}ms ${SIDEBAR_TRANSITION_EASING}`,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isSettingsActive(item) ? 'white' : 'text.secondary',
                        minWidth: isDesktopCollapsed ? 0 : { xs: 36, sm: 40 },
                        width: isDesktopCollapsed ? '100%' : 'auto',
                        mr: isDesktopCollapsed ? 0 : undefined,
                        transition: `all ${SIDEBAR_TRANSITION_DURATION_MS}ms ${SIDEBAR_TRANSITION_EASING}`,
                      }}
                    >
                      <Tooltip
                        title={getDisplayLabel(item.text)}
                        placement="right"
                        disableHoverListener={!isDesktopCollapsed}
                        disableFocusListener={!isDesktopCollapsed}
                        disableTouchListener={!isDesktopCollapsed}
                      >
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          {item.icon}
                        </Box>
                      </Tooltip>
                    </ListItemIcon>
                    {!isDesktopCollapsed && (
                      <ListItemText
                        primary={getDisplayLabel(item.text)}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: { xs: '0.875rem', sm: '0.9rem' },
                            fontWeight: isSettingsActive(item) ? 'bold' : 'normal',
                          },
                        }}
                      />
                    )}
                    {activityTrackingRunning && item.text === 'Attendance' && !isDropdownOpen('attendance') ? (
                      <VideoCameraFrontIcon
                        sx={{
                          ml: 0.5,
                          mr: 0.5,
                          fontSize: 11,
                          color: '#f97316',
                          filter: 'drop-shadow(0 0 4px rgba(249, 115, 22, 0.8))',
                          animation: 'sidebarRecordingPulse 1.25s ease-in-out infinite',
                          '@keyframes sidebarRecordingPulse': {
                            '0%': { opacity: 0.55, transform: 'scale(0.9)' },
                            '50%': { opacity: 1, transform: 'scale(1.12)' },
                            '100%': { opacity: 0.55, transform: 'scale(0.9)' },
                          },
                        }}
                      />
                    ) : null}
                    {!isDesktopCollapsed && (
                      isDropdownOpen(item.dropdownKey) ? (
                        <ExpandLessIcon sx={{ color: isSettingsActive(item) ? 'white' : 'text.secondary' }} />
                      ) : (
                        <ExpandMoreIcon sx={{ color: isSettingsActive(item) ? 'white' : 'text.secondary' }} />
                      )
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse in={!isDesktopCollapsed && isDropdownOpen(item.dropdownKey)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItem key={subItem.text} disablePadding>
                          <ListItemButton
                            onClick={() => handleNavigation(subItem.path)}
                            sx={{
                              backgroundColor: isActive(subItem.path) ? 'primary.main' : 'transparent',
                              color: isActive(subItem.path) ? 'white' : 'text.primary',
                              '&:hover': {
                                backgroundColor: isActive(subItem.path) ? 'primary.dark' : 'action.hover',
                              },
                              mx: { xs: 0.5, sm: 1 },
                              ml: { xs: 3, sm: 5 },
                              borderRadius: 1,
                              mb: 0.5,
                              py: { xs: 0.75, sm: 1 },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: isActive(subItem.path) ? 'white' : 'text.secondary',
                                minWidth: { xs: 32, sm: 40 },
                              }}
                            >
                              {subItem.icon}
                            </ListItemIcon>
                        <ListItemText
                              primary={getDisplayLabel(subItem.text)}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontSize: { xs: '0.8rem', sm: '0.85rem' },
                                  fontWeight: isActive(subItem.path) ? 'bold' : 'normal',
                                },
                              }}
                            />
                            {activityTrackingRunning && item.text === 'Attendance' && subItem.text === 'Activity' && isDropdownOpen('attendance') ? (
                              <VideoCameraFrontIcon
                                sx={{
                                  ml: 0.5,
                                  fontSize: 11,
                                  color: '#f97316',
                                  filter: 'drop-shadow(0 0 4px rgba(249, 115, 22, 0.8))',
                                  animation: 'sidebarRecordingPulse 1.25s ease-in-out infinite',
                                  '@keyframes sidebarRecordingPulse': {
                                    '0%': { opacity: 0.55, transform: 'scale(0.9)' },
                                    '50%': { opacity: 1, transform: 'scale(1.12)' },
                                    '100%': { opacity: 0.55, transform: 'scale(0.9)' },
                                  },
                                }}
                              />
                            ) : null}
                          </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    justifyContent: isDesktopCollapsed ? 'center' : 'flex-start',
                    backgroundColor: isActive(item.path) ? 'primary.main' : 'transparent',
                    color: isActive(item.path) ? 'white' : 'text.primary',
                    '&:hover': {
                      backgroundColor: isActive(item.path) ? 'primary.dark' : 'action.hover',
                    },
                    mx: { xs: 0.5, sm: 1 },
                    borderRadius: 1,
                    mb: 0.5,
                    py: { xs: 1, sm: 1.5 },
                    px: isDesktopCollapsed ? 0.75 : undefined,
                    transition: `padding ${SIDEBAR_TRANSITION_DURATION_MS}ms ${SIDEBAR_TRANSITION_EASING}`,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isActive(item.path) ? 'white' : 'text.secondary',
                      minWidth: isDesktopCollapsed ? 0 : { xs: 36, sm: 40 },
                      width: isDesktopCollapsed ? '100%' : 'auto',
                      mr: isDesktopCollapsed ? 0 : undefined,
                      transition: `all ${SIDEBAR_TRANSITION_DURATION_MS}ms ${SIDEBAR_TRANSITION_EASING}`,
                    }}
                  >
                    <Tooltip
                      title={getDisplayLabel(item.text)}
                      placement="right"
                      disableHoverListener={!isDesktopCollapsed}
                      disableFocusListener={!isDesktopCollapsed}
                      disableTouchListener={!isDesktopCollapsed}
                    >
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        {item.icon}
                      </Box>
                    </Tooltip>
                  </ListItemIcon>
                    {!isDesktopCollapsed && (
                      <ListItemText
                        primary={getDisplayLabel(item.text)}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: { xs: '0.875rem', sm: '0.9rem' },
                            fontWeight: isActive(item.path) ? 'bold' : 'normal',
                          },
                        }}
                      />
                    )}
                  {activityTrackingRunning && item.text === 'Attendance' ? (
                    <VideoCameraFrontIcon
                      sx={{
                        ml: 0.5,
                        fontSize: 11,
                        color: '#f97316',
                        filter: 'drop-shadow(0 0 4px rgba(249, 115, 22, 0.8))',
                        animation: 'sidebarRecordingPulse 1.25s ease-in-out infinite',
                        '@keyframes sidebarRecordingPulse': {
                          '0%': { opacity: 0.55, transform: 'scale(0.9)' },
                          '50%': { opacity: 1, transform: 'scale(1.12)' },
                          '100%': { opacity: 0.55, transform: 'scale(0.9)' },
                        },
                      }}
                    />
                  ) : null}
                </ListItemButton>
              </ListItem>
            )}
          </React.Fragment>
        ))
        )}

        <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              justifyContent: isDesktopCollapsed ? 'center' : 'flex-start',
              backgroundColor: 'transparent',
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.light',
                color: 'white',
              },
              mx: { xs: 0.5, sm: 1 },
              borderRadius: 1,
              py: { xs: 1, sm: 1.5 },
              px: isDesktopCollapsed ? 0.75 : undefined,
              transition: `padding ${SIDEBAR_TRANSITION_DURATION_MS}ms ${SIDEBAR_TRANSITION_EASING}`,
            }}
          >
            <ListItemIcon
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'error.main',
                minWidth: isDesktopCollapsed ? 0 : { xs: 36, sm: 40 },
                width: isDesktopCollapsed ? '100%' : 'auto',
                mr: isDesktopCollapsed ? 0 : undefined,
                transition: `all ${SIDEBAR_TRANSITION_DURATION_MS}ms ${SIDEBAR_TRANSITION_EASING}`,
              }}
            >
              <Tooltip
                title="Logout"
                placement="right"
                disableHoverListener={!isDesktopCollapsed}
                disableFocusListener={!isDesktopCollapsed}
                disableTouchListener={!isDesktopCollapsed}
              >
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <LogoutIcon />
                </Box>
              </Tooltip>
            </ListItemIcon>
            {!isDesktopCollapsed && (
              <ListItemText
                primary="Logout"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: { xs: '0.875rem', sm: '0.9rem' },
                    fontWeight: 'normal',
                  },
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </List>
      <Box
        sx={{
          px: isDesktopCollapsed ? 1 : 2,
          py: 1.25,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          {isDesktopCollapsed ? `v${backendVersion}` : `kivo eye v${backendVersion}`}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: { xs: drawerWidth, sm: drawerWidth },
            backgroundColor: '#f5f5f5',
            borderRight: '1px solid #e0e0e0',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      
      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: currentDrawerWidth,
          flexShrink: 0,
          transition: `width ${SIDEBAR_TRANSITION_DURATION_MS}ms ${SIDEBAR_TRANSITION_EASING}`,
          '& .MuiDrawer-paper': {
            width: currentDrawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#f5f5f5',
            borderRight: '1px solid #e0e0e0',
            overflowX: 'hidden',
            willChange: 'width',
            transition: `width ${SIDEBAR_TRANSITION_DURATION_MS}ms ${SIDEBAR_TRANSITION_EASING}`,
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
